import React, { type FC, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { IS_BIZ_USER } from 'reactApp/appHelpers/configHelpers';
import { shouldShowCloneButtonForCloudAttach } from 'reactApp/appHelpers/featuresHelpers/features/cloneButtonForCloudAttach';
import { editDocumentsInAttachesExts } from 'reactApp/appHelpers/featuresHelpers/features/editDocumentsInAttachesExts';
import { type AttachesItem, EAttachTypes } from 'reactApp/modules/attaches/attaches.types';
import { PromoSelectors } from 'reactApp/modules/promo/promo.selectors';
import { EPromoType } from 'reactApp/modules/promo/promo.types';
import { EditorRestrictionTooltipWrapper } from 'reactApp/ui/EditorRestriction/EditorRestrictionTooltip/EditorRestrictionTooltipWrapper';
import { sendViewerDwh } from 'reactApp/ui/ReactViewer/ReactViewer.helpers';
import { AttachMenuInfo } from 'reactApp/ui/ReactViewer/ViewerMenu/AttachMenuInfo/AttachMenuInfo';
import { CloneMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/CloneMenuButton/CloneMenuButton';
import { CloseMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/CloseMenuButton/CloseMenuButton';
import { DownloadMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/DownloadMenuButton/DownloadMenuButton';
import { EditDocumentCopyTooltip } from 'reactApp/ui/ReactViewer/ViewerMenu/EditDocumentCopyTooltip/EditDocumentCopyTooltip';
import { EditMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/EditMenuButton/EditMenuButton';
import { MenuLogo } from 'reactApp/ui/ReactViewer/ViewerMenu/MenuLogo/MenuLogo';
import { MenuItemName, MoreMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/MoreMenuButton/MoreMenuButton';
import { ShareMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/ShareMenuButton/ShareMenuButton';
import {
    type ViewerMenuProps,
    ViewerMenuCenter,
    ViewerMenuEnd,
    ViewerMenuStart,
    ViewerMenuWrapper,
} from 'reactApp/ui/ReactViewer/ViewerMenu/ViewerMenu';
import { Zoomer } from 'reactApp/ui/ReactViewer/ViewerMenu/Zoomer/Zoomer';
import { canCloneWeblink } from 'reactApp/utils/cloneHelpers';

function showEditTooltipForExt(ext: string) {
    return editDocumentsInAttachesExts.includes(ext);
}

interface Props extends ViewerMenuProps {
    file: AttachesItem;
}

export const AttachMenu: FC<Props> = ({
    file,
    fileStorage,
    isAuthorized,
    showImageEdit,
    isArchiveItem,
    zoom,
    decreaseZoom,
    increaseZoom,
    onFileClick,
    onActive,
    showDownload,
    onDownload,
    onPublish,
    onClone,
    onImageEdit,
    showEdit,
    onAttachCopyEdit,
    showPdfEdit,
    onPdfAttachCopyEdit,
    allowPrinting,
    onPrint,
    showClose,
    onClose,
    isEditingDisabled,
}) => {
    const showClone = canCloneWeblink(isAuthorized, fileStorage);
    const isTemporary = file.attachType === EAttachTypes.temporary;
    const isCloudAttach = file.attachType === EAttachTypes.cloud;
    const showAttachEditButton = showEdit && !isTemporary && !isCloudAttach && !IS_BIZ_USER;
    const showAttachCloneButton = showClone && !isArchiveItem && !isTemporary && (!isCloudAttach || shouldShowCloneButtonForCloudAttach);
    const showAttachShareButton = showClone && !isArchiveItem && !isTemporary && !isCloudAttach;

    const editButtonRef = useRef<HTMLDivElement>(null);
    const onEditButtonClick = () => {
        if (isEditingDisabled) {
            return;
        }
        onAttachCopyEdit(file);
    };

    const { ext: extension } = file;
    const editDocumentsInAttachesTooltip = useSelector(PromoSelectors.getPromo(EPromoType.editDocumentsInAttachesTooltip));
    const showEditDocumentsInAttachesTooltip =
        editDocumentsInAttachesTooltip && showAttachEditButton && showEditTooltipForExt(extension) && !isEditingDisabled;

    useEffect(() => {
        if (!showEditDocumentsInAttachesTooltip) {
            if (isEditingDisabled) {
                editDocumentsInAttachesTooltip?.onEditDisabled();
            }

            return;
        }

        editDocumentsInAttachesTooltip?.onShow();
        sendViewerDwh({
            action: 'edit-promo-show',
            extension,
        });
    }, [showEditDocumentsInAttachesTooltip, isEditingDisabled, editDocumentsInAttachesTooltip, extension]);

    const moreItems = useMemo(
        () => [
            ...(allowPrinting
                ? [
                      {
                          name: MenuItemName.PRINT,
                          onClick: () => {
                              onPrint(file);
                          },
                      },
                  ]
                : []),
        ],
        [allowPrinting, onPrint]
    );

    return (
        <ViewerMenuWrapper>
            <ViewerMenuStart>
                <MenuLogo />
                <AttachMenuInfo file={file} onClick={onFileClick} onActive={onActive} />
            </ViewerMenuStart>
            <ViewerMenuCenter>{zoom && <Zoomer value={zoom} decrease={decreaseZoom} increase={increaseZoom} />}</ViewerMenuCenter>
            <ViewerMenuEnd>
                {showAttachShareButton && (
                    <>
                        <ShareMenuButton
                            onClick={() => {
                                onPublish(file);
                            }}
                        />
                        {showImageEdit && (
                            <EditMenuButton
                                onClick={() => {
                                    onImageEdit(file);
                                }}
                            />
                        )}
                    </>
                )}
                {showAttachEditButton && (
                    <EditorRestrictionTooltipWrapper isEditingDisabled={isEditingDisabled}>
                        <EditMenuButton
                            hint="Редактировать копию"
                            qaId="edit_public_copy"
                            onClick={onEditButtonClick}
                            ref={editButtonRef}
                        />
                    </EditorRestrictionTooltipWrapper>
                )}
                {showEditDocumentsInAttachesTooltip && (
                    <EditDocumentCopyTooltip
                        target={editButtonRef}
                        onClick={onEditButtonClick}
                        onClose={() => {
                            editDocumentsInAttachesTooltip?.onClose();
                        }}
                        onCrossClose={() => {
                            editDocumentsInAttachesTooltip?.onCrossClose();
                        }}
                    />
                )}
                {showPdfEdit && (
                    <EditMenuButton
                        hint="Редактировать копию"
                        qaId="edit"
                        onClick={() => {
                            onPdfAttachCopyEdit(file);
                        }}
                    />
                )}
                {showAttachCloneButton && <CloneMenuButton onClick={onClone} />}
                {showDownload && (
                    <DownloadMenuButton
                        file={file}
                        onClick={() => {
                            onDownload(file);
                        }}
                    />
                )}
                <MoreMenuButton list={moreItems} />
                {showClose && (
                    <CloseMenuButton
                        onClick={() => {
                            onClose(file);
                        }}
                    />
                )}
            </ViewerMenuEnd>
        </ViewerMenuWrapper>
    );
};
